import { useEffect, useMemo, useState } from "react";
import { RestartAltOutlined } from "@mui/icons-material";
import { formatDateTime } from "../../../../Utils/formatDateTime";
import { getModelTrainingHistory } from "../../../../services/Dashboard/Table";

function normalizeWeightageRows(featureImportance) {
  if (!featureImportance) return [];
  const rows = Array.isArray(featureImportance)
    ? featureImportance.map((item, index) => {
      if (Array.isArray(item)) return { feature: item[0] ?? `Feature ${index + 1}`, value: item[1] };
      if (item && typeof item === "object") return { feature: item.feature ?? item.name ?? item.label ?? item.key ?? `Feature ${index + 1}`, value: item.importance ?? item.weightage ?? item.weight ?? item.value ?? item.score };
      return { feature: `Feature ${index + 1}`, value: item };
    })
    : Object.entries(featureImportance).map(([feature, value]) => ({ feature, value }));

  return rows
    .map((row) => { const numericValue = Number(row.value); return { feature: String(row.feature || "Unnamed feature"), value: row.value, numericValue: Number.isFinite(numericValue) ? numericValue : null }; })
    .filter((row) => row.value !== undefined && row.value !== null)
    .sort((left, right) => {
      if (left.numericValue !== null && right.numericValue !== null) return right.numericValue - left.numericValue;
      return left.feature.localeCompare(right.feature);
    });
}

function formatWeightageValue(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);
  if (Number.isInteger(numericValue)) return String(numericValue);
  return numericValue.toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
}

function ModelWeightageModule() {
  const [trainingHistory, setTrainingHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchTrainingHistory = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const data = await getModelTrainingHistory();
      setTrainingHistory(data);
    } catch (error) {
      setTrainingHistory(null);
      setErrorMessage(error?.message || error?.detail || error?.error || "Unable to load model weightage values.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let ignore = false;
    const loadTrainingHistory = async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const data = await getModelTrainingHistory();
        if (!ignore) setTrainingHistory(data);
      } catch (error) {
        if (!ignore) { setTrainingHistory(null); setErrorMessage(error?.message || error?.detail || error?.error || "Unable to load model weightage values."); }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    loadTrainingHistory();
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const weightageRows = useMemo(() => normalizeWeightageRows(trainingHistory?.feature_importance), [trainingHistory]);

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)]">
        <div className="flex flex-col gap-1">
          <p className="md:text-sm text-xs label-caps block m-0">Latest Training Run</p>
          <h4 className="xl:text-xl md:text-lg text-base text-[var(--color-text)]">
            {trainingHistory?.model_train_date ? formatDateTime(trainingHistory.model_train_date) : "No training run loaded"}
          </h4>
          <p className="xl:text-sm md:text-xs text-[10px] text-[var(--color-text-muted)]">{weightageRows.length} weightage value{weightageRows.length === 1 ? "" : "s"}</p>
        </div>
        <button type="button" onClick={fetchTrainingHistory} disabled={loading} className={`inline-flex items-center gap-1.5 min-h-[38px] px-3 py-2.5 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-transparent text-[var(--color-text)] md:text-sm text-xs font-bold ${loading ? "cursor-not-allowed" : "cursor-pointer"}`}>
          <RestartAltOutlined sx={{ fontSize: 17 }} />Refresh
        </button>
      </div>

      {errorMessage && <div className="px-4 py-3.5 border border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] rounded-[var(--radius-md)] text-[var(--color-accent-red)] md:text-sm text-xs">{errorMessage}</div>}

      <div className="border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)] overflow-hidden">
        {loading ? (
          <div className="grid gap-px">{[1, 2, 3, 4].map((item) => <div key={item} className="skeleton h-[70px] bg-[rgba(255,255,255,0.04)]" />)}</div>
        ) : weightageRows.length === 0 ? (
          <div className="min-h-[180px] grid place-items-center xl:text-xl md:text-lg text-base text-[var(--color-text-muted)] text-center">No model weightage values found.</div>
        ) : (
          weightageRows.map((row, index) => (
            <div key={`${row.feature}-${index}`} className="admin-weightage-row grid grid-cols-[minmax(0,1fr)_minmax(120px,0.28fr)] gap-4 justify-between items-center min-h-[72px] p-4 first:border-t-0 border-t border-[var(--color-border)]">
              <div className="min-w-0">
                <h5 className="m-0 text-[var(--color-text)] xl:text-lg md:text-base text-sm font-black truncate" title={row.feature}>{row.feature.replaceAll("_", " ")}</h5>
              </div>
              <p className="m-0 text-[var(--color-primary)] xl:text-base md:text-sm text-xs font-black text-right">{formatWeightageValue(row.value)}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ModelWeightageModule;
